import { Injectable } from '@angular/core';
import { getDoc, doc, collection, setDoc, updateDoc, query, where, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Account } from 'src/app/models/account';
import { Organization } from 'src/app/models/organization';
import { State } from 'src/app/models/state';

import { DataStoreService } from 'src/app/services/data-store/data-store.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { UserService } from 'src/app/services/user/user.service';

export enum AccountErrorCodes {
  AccountNotFound = 'account-not-found',
  UserNotFound = 'user-not-found',
  UserDataNotFound = 'user-data-not-found',
  UserNotLoggedIn = 'user-not-logged-in',
  ClusterNotFound = 'cluster-not-found',
  Unknown = 'unknown',
}

export class AccountError extends Error {
  constructor(readonly code: AccountErrorCodes) {
    super(code);
    this.name = 'AccountError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  constructor(
    private dataStore: DataStoreService,
    private firebase: FirebaseService,
    private userService: UserService,
  ) {
    // Clear all caches on logout (not on initial empty state before Firebase auth initializes)
    this.dataStore.getState$().subscribe(({ newState, oldState }) => {
      if (!newState.authUser && oldState?.authUser) {
        this.clearCurrentAccount();
        this.clearSelectedOrg();
      }
    });
  }

  async getAccount(accountId: string): Promise<Account> {
    if (!this.firebase.auth.currentUser) {
      throw new AccountError(AccountErrorCodes.UserNotLoggedIn);
    }

    const accountDoc = await getDoc(doc(this.firebase.firestore, 'developers', accountId));
    const data = accountDoc.data();

    if (!data) {
      throw new AccountError(AccountErrorCodes.AccountNotFound);
    }

    const { name, ownerId, vendorId, organizationId, authorizedDomains, features, createdAt, creditBalance, product } = data;

    if (!name) {
      throw new AccountError(AccountErrorCodes.AccountNotFound);
    }

    return {
      id: accountId,
      name,
      ownerId,
      vendorId,
      organizationId,
      authorizedDomains,
      features,
      createdAt,
      creditBalance,
      product,
    };
  }

  async getCurrentAccount(forceFetch = false): Promise<Account> {
    const inMemoryAccount = this.dataStore.getProperty('currentAccount');
    if (!forceFetch && inMemoryAccount) {
      return inMemoryAccount;
    }

    await this.firebase.auth.authStateReady();

    if (!this.firebase.auth.currentUser) {
      throw new AccountError(AccountErrorCodes.UserNotLoggedIn);
    }

    const uid = this.firebase.auth.currentUser.uid;
    const user = await this.userService.getCurrentUserDocument();
    const orgIds = user?.organizationIds || [];

    // Legacy users may not have organizationIds — fall back to defaultAccountId/currentAccountId
    if (!orgIds.length) {
      const accountId = user?.defaultAccountId || user?.currentAccountId;
      if (!accountId) {
        throw new AccountError(AccountErrorCodes.AccountNotFound);
      }

      const legacyCacheKey = `crewdle_account_${uid}_${accountId}`;
      if (!forceFetch) {
        const cached = sessionStorage.getItem(legacyCacheKey);
        if (cached) {
          const account = AccountService.stripKeys(JSON.parse(cached));
          this.dataStore.updateState({ currentAccount: account });
          return account;
        }
      }

      const currentAccount = await this.getAccount(accountId);

      sessionStorage.setItem(legacyCacheKey, JSON.stringify(currentAccount));
      this.dataStore.updateState({ currentAccount });
      return currentAccount;
    }

    // The user-doc `currentOrganizationId` is the cross-app source of truth
    // (shared with admin/connect/open-webui); then this app's sessionStorage
    // preference (trusted even if not in organizationIds — legacy data gap),
    // then the first org.
    const remoteOrgId = (user as { currentOrganizationId?: string } | undefined)?.currentOrganizationId;
    const storedOrgId = sessionStorage.getItem(`crewdle_selected_org_${uid}`);
    const orgId = remoteOrgId || storedOrgId || orgIds[0];

    const account = await this.resolveOrgAccount(uid, orgId, forceFetch);
    if (account) {
      return account;
    }

    console.warn('[AccountService] no account found for org', orgId, '— asking the backend to provision one');
    // Org has no studio account — the backend provisions it (the browser never creates keys)
    const newAccount = await this.ensureStudioAccount(orgId);
    const cacheKey = `crewdle_account_${uid}_${orgId}`;
    sessionStorage.setItem(cacheKey, JSON.stringify(newAccount));
    this.dataStore.updateState({ currentAccount: newAccount });
    return newAccount;
  }

  clearCurrentAccount(): void {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('crewdle_account_')) {
        sessionStorage.removeItem(key);
      }
    }
  }

  private async resolveOrgAccount(uid: string, orgId: string, forceFetch: boolean): Promise<Account | null> {
    const cacheKey = `crewdle_account_${uid}_${orgId}`;
    if (!forceFetch) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const account = AccountService.stripKeys(JSON.parse(cached));
        this.dataStore.updateState({ currentAccount: account });
        return account;
      }
    }

    // Try querying by organizationId first; fall back to owner-based lookup when
    // Firestore rules deny cross-org queries (e.g. user is member but not account owner).
    let accounts: Account[];
    try {
      accounts = await this.listAccountsByOrganization(orgId);
    } catch {
      const ownedAccounts = await this.listAccountsByOwner(uid);
      accounts = ownedAccounts.filter(a => a.organizationId === orgId);
    }
    if (!accounts.length) return null;

    // Prefer studio account; fall back to legacy accounts (no product field), then any account
    const currentAccount =
      accounts.find(a => a.product === 'studio') ||
      accounts.find(a => !a.product) ||
      accounts[0];

    sessionStorage.setItem(cacheKey, JSON.stringify(currentAccount));
    this.dataStore.updateState({ currentAccount });
    return currentAccount;
  }

  private clearSelectedOrg(): void {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('crewdle_selected_org_')) {
        sessionStorage.removeItem(key);
      }
    }
  }

  async switchOrganization(orgId: string): Promise<void> {
    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) return;
    sessionStorage.setItem(`crewdle_selected_org_${uid}`, orgId);
    // Persist the shared selection on the user doc BEFORE reloading (shared with
    // admin/connect/open-webui). getCurrentAccount() now prefers this field, so
    // without it the reload would restore the previous shared org.
    try {
      await setDoc(doc(this.firebase.firestore, 'users', uid), { currentOrganizationId: orgId }, { merge: true });
    } catch {
      // Non-blocking — sessionStorage still reflects the choice for this app.
    }
    this.clearCurrentAccount();

    // If the current URL contains a resource ID (e.g. /workflow/:id), strip it
    // so the new org doesn't try to load a resource that belongs to the old org.
    // Routes like /editor and /log-explorer are always redirected to /workflow.
    const segments = window.location.pathname.split('/').filter(s => s);
    const workflowRedirects = ['editor', 'log-explorer', 'chat'];
    let targetPath: string;
    if (segments.length === 0 || workflowRedirects.includes(segments[0])) {
      targetPath = '/workflow';
    } else if (segments.length > 1) {
      targetPath = '/' + segments[0]; // strip :id (and anything after)
    } else {
      targetPath = window.location.pathname; // already at base, just reload
    }
    window.location.href = targetPath;
  }

  getAccountRef(accountId: string) {
    return doc(this.firebase.firestore, 'developers', accountId);
  }

  updateOrganizationOnAccount(organizationId: string) {
    const account = this.dataStore.getProperty('currentAccount');
    if (!account) {
      throw new Error('AccountService: Current account not set');
    }
    updateDoc(this.getAccountRef(account.id), { organizationId });
  }

  /**
   * Product accounts and their keys are created only by the backend. This asks
   * `provisionProductAccount` for the org's Forge ('ai-studio') account (an
   * idempotent backfill) and loads it. No key material is created or read here.
   */
  async ensureStudioAccount(organizationId: string): Promise<Account> {
    if (!this.firebase.auth.currentUser) {
      throw new Error('AccountService: User not logged in');
    }
    const provision = httpsCallable<{ organizationId: string; productId: string }, { accountId: string | null }>(
      this.firebase.functions,
      'provisionProductAccount',
    );
    const { data } = await provision({ organizationId, productId: 'ai-studio' });
    if (!data.accountId) {
      throw new AccountError(AccountErrorCodes.AccountNotFound);
    }
    return this.getAccount(data.accountId);
  }

  /** Drops the legacy key fields that older sessions cached in sessionStorage. */
  private static stripKeys(account: Account & { secretKey?: unknown; accessToken?: unknown }): Account {
    const { secretKey: _s, accessToken: _a, ...rest } = account;
    return rest as Account;
  }

  async updateAccountInState(account: Account, organization?: Organization) {
    if (account.organizationId) {
      const uid = this.firebase.auth.currentUser?.uid;
      if (uid) {
        sessionStorage.setItem(`crewdle_account_${uid}_${account.organizationId}`, JSON.stringify(account));
      }
    }
    const state: Partial<State> = { currentAccount: account };
    if (organization) {
      state['organization'] = organization;
    }
    this.dataStore.updateState(state);
  }

  async listAccountsByOwner(uid: string): Promise<Account[]> {
    const db = this.firebase.firestore;
    const accountsRef = collection(db, 'developers');
    const q = query(accountsRef, where('ownerId', '==', uid));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return [];
    }

    return querySnapshot.docs.map(d => {
      const data = d.data();
      const { name, ownerId, vendorId, organizationId, authorizedDomains, features, createdAt, product } = data;
      return { id: d.id, name, ownerId, vendorId, organizationId, authorizedDomains, features, createdAt, product };
    });
  }

  async listAccountsByOrganization(organizationId: string): Promise<Account[]> {
    const db = this.firebase.firestore;
    const accountsRef = collection(db, 'developers');
    const q = query(accountsRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return [];
    }

    return querySnapshot.docs.map(d => {
      const data = d.data();
      const { name, ownerId, vendorId, organizationId, authorizedDomains, features, createdAt, product } = data;
      return {
        id: d.id,
        name,
        ownerId,
        vendorId,
        organizationId,
        authorizedDomains,
        features,
        createdAt,
        product,
      };
    });
  }

  async listCurrentOrganizationAccounts(): Promise<Account[]> {
    const currentAccount = await this.getCurrentAccount();
    if (!currentAccount.organizationId) {
      return [currentAccount];
    }

    let accounts: Account[];
    try {
      accounts = await this.listAccountsByOrganization(currentAccount.organizationId);
    } catch {
      const uid = this.firebase.auth.currentUser?.uid;
      accounts = uid
        ? (await this.listAccountsByOwner(uid)).filter(account => account.organizationId === currentAccount.organizationId)
        : [];
    }

    const byId = new Map<string, Account>();
    for (const account of [currentAccount, ...accounts]) {
      byId.set(account.id, account);
    }

    return Array.from(byId.values()).sort((a, b) => {
      if (a.id === currentAccount.id) return -1;
      if (b.id === currentAccount.id) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }

  async updateAccountName(name: string) {
    let currentAccount = this.dataStore.getProperty('currentAccount');
    if (!currentAccount) {
      currentAccount = await this.getCurrentAccount();
    }

    try {
      await setDoc(doc(this.firebase.firestore, 'developers', currentAccount.id), { name }, { merge: true });
      currentAccount.name = name;
      this.dataStore.updateState({ currentAccount });
    } catch (e) {
      console.error(e);
      throw new Error('AccountService: Failed to update account name');
    }
  }

  async addAuthorizedDomain(domain: string) {
    const currentAccount = await this.getCurrentAccount();
    await setDoc(doc(this.firebase.firestore, 'developers', currentAccount.id), {
      authorizedDomains: arrayUnion(domain),
    }, { merge: true });
    this.dataStore.updateState({ currentAccount });
  }

  async findClusterIdFromAIContext(genAIContextId: string): Promise<string> {
    const currentAccount = await this.getCurrentAccount();
    const mistletsRef = collection(this.firebase.firestore, 'developers', currentAccount.id, 'resources', 'agent', 'items');
    const querySnapshot = await getDocs(mistletsRef);

    for (const doc of querySnapshot.docs) {
      const { clusters = [] } = doc.data();
      for (const cluster of clusters) {
        for (const contextId of cluster.generativeAIContexts) {
          if (contextId === genAIContextId) {
            return cluster.id;
          }
        }
      }
    }

    throw new AccountError(AccountErrorCodes.ClusterNotFound);
  }

  async removeAuthorizedDomain(domain: string) {
    const currentAccount = await this.getCurrentAccount();
    await setDoc(doc(this.firebase.firestore, 'developers', currentAccount.id), {
      authorizedDomains: arrayRemove(domain),
    }, { merge: true });
  }
}
